import { Check, ChevronsUpDown, LogOut, Monitor, Moon, Plus, Sun } from 'lucide-react'
import { NavLink } from 'react-router'
import { DevelopedBy } from '@/components/brand/developed-by'
import { Logo, LogoMark } from '@/components/brand/logo'
import { UserAvatar } from '@/components/common/avatars'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ROLE_META } from '@/domain/vacations'
import { cn } from '@/lib/utils'
import { useSession } from '../session'
import { useTheme, type ThemePreference } from '../theme'
import { useWs } from '../workspace'
import { NAV_ADMIN, NAV_PRIMARY, NAV_TEAM, type NavItem } from './nav'

function Item({ item, count }: { item: NavItem; count?: number }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'group flex h-8 items-center gap-2.5 rounded-md px-2 text-sm text-fg-muted transition-colors hover:bg-hover hover:text-fg',
          isActive && 'bg-surface text-fg shadow-xs ring-1 ring-border',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate">{item.label}</span>
      {count ? (
        <span className="min-w-5 rounded-full bg-accent-soft px-1.5 text-center text-2xs font-semibold text-accent tabular" aria-label={`${count} por decidir`}>
          {count}
        </span>
      ) : null}
    </NavLink>
  )
}

function Group({ title, items, counts }: { title?: string; items: NavItem[]; counts?: Record<string, number> }) {
  if (!items.length) return null
  return (
    <div className="grid gap-0.5">
      {title && <p className="px-2 pb-1 text-2xs font-medium tracking-wider text-fg-subtle uppercase">{title}</p>}
      {items.map((i) => (
        <Item key={i.to} item={i} count={counts?.[i.to]} />
      ))}
    </div>
  )
}

export function Sidebar() {
  const ws = useWs()
  const { memberships, switchOrg, session, signOut } = useSession()
  const { preference, setPreference } = useTheme()
  const visible = (items: NavItem[]) => items.filter((i) => !i.permission || ws.can(i.permission))
  const pending = ws.toDecide.requests.length + ws.toDecide.changes.length
  const user = session?.user

  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-bg md:flex" aria-label="Navegação principal">
      <div className="flex h-12 items-center px-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-hover" aria-label="Mudar de organização">
              <LogoMark />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{ws.data.organization.name}</span>
              </span>
              <ChevronsUpDown className="size-3.5 text-fg-subtle" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60">
            <DropdownMenuLabel>Organizações</DropdownMenuLabel>
            {(memberships ?? []).map((m) => (
              <DropdownMenuItem key={m.organization.id} onSelect={() => switchOrg(m.organization.id)}>
                <span className="flex-1 truncate">{m.organization.name}</span>
                {m.organization.id === ws.orgId && <Check />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <NavLink to="/onboarding">
                <Plus /> Criar nova organização
              </NavLink>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-2 scrollbar-thin">
        <Group items={NAV_PRIMARY} />
        <Group title="Equipa" items={visible(NAV_TEAM)} counts={{ '/approvals': pending }} />
        <Group title="Administração" items={visible(NAV_ADMIN)} />
      </nav>

      <div className="border-t border-border p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left hover:bg-hover" aria-label="Menu do utilizador">
              <UserAvatar name={ws.me?.name ?? user?.name ?? user?.email} id={ws.me?.id} size="md" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{ws.me?.name ?? user?.name}</span>
                <span className="block truncate text-xs text-fg-subtle">{ROLE_META[ws.data.role].label}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60" side="top">
            <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Aparência</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
              <DropdownMenuRadioItem value="system">
                <Monitor /> Sistema
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="light">
                <Sun /> Claro
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                <Moon /> Escuro
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void signOut()}>
              <LogOut /> Terminar sessão
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <p className="mt-2 hidden px-1.5 text-2xs text-fg-subtle lg:block">
          <Logo className="[&_svg]:size-3.5 [&_svg]:opacity-60 [&>span>span]:text-[11px]" />
          <DevelopedBy className="mt-1 block" />
        </p>
      </div>
    </aside>
  )
}
